import styled from "styled-components";
import { borderRadius, rawColors } from "@magicbi/ui-library";
import { spacing } from "../../../design-system/tokens/spacing";
import { mediaQueries } from "../../../design-system/tokens/breakpoints";
import { duration, easing } from "../../../design-system/tokens/transitions";
import { shadows } from "../../../design-system/tokens/shadows";
import { typographyMixin, fontSizes, fontWeights } from "../../../design-system/tokens/typography";
import { StyledTextarea } from "../../atoms/Textarea/Textarea.styles";

/**
 * GAP: top-accent gradient has no token. Stops from the Figma "Linear Gradient"
 * variable (#FFFFFF, #FFFFFF, #69ADF6, #6C3ADF); purple sits at the left edge
 * per the design, so the angle is 270deg.
 */
const TOP_ACCENT_GRADIENT =
  "linear-gradient(90deg, #6C3ADF 0%, #69ADF6 65%, #FFFFFF 100%)";

/**
 * ─── Central spacing config for the StoryBuilder canvas ─────────────────────
 *
 * Tweak only here — every gap / padding in this file reads from these values.
 *
 *   sectionGap         gap between section cards in the scroll list
 *   sectionPadTop      top padding inside each section card
 *   sectionPadBottom   bottom padding inside each section card
 *   sectionPadX        left/right padding for section header + body
 *   sectionInnerGap    gap between the section header row and the body
 *   headerBottomPad    breathing room below the page title before first card
 * ─────────────────────────────────────────────────────────────────────────── */
const S = {
  sectionGap: spacing[3], // GAP: design gap 14px has no spacing token; 12px used
  sectionPadTop: spacing[4], // 16px — ① top padding inside a card
  sectionPadBottom: spacing[4], // 16px — ③ bottom padding inside a card (F05: 16 18)
  sectionPadX: spacing[4], // 16px — ← → left/right padding
  sectionInnerGap: spacing[2.5], // 10px — ② header row ↕ body gap (F05)
  headerBottomPad: spacing[3], // 12px — page title → first card
} as const;

/** Figma 3812:42015 — white canvas card, radius 12, padding 28 / 12 / 12. */
export const Card = styled.section`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
  box-sizing: border-box;
  padding: 0;
  border-radius: ${borderRadius.xl};
  background: ${rawColors.structural.white};
`;

export const ProgressBar = styled.div<{ $progress?: number }>`
  position: absolute;
  top: 0;
  left: 0;
  height: 3px;
  width: ${({ $progress = 1 }) => Math.min(100, Math.max(0, $progress * 100))}%;
  background: ${TOP_ACCENT_GRADIENT};
  border-radius: ${borderRadius.xl} ${borderRadius.xl} 0 0;
  z-index: 10;
  transition: width ${duration.normal} ${easing.smooth};
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/** Scrollable story canvas — fills space above the CTA. */
export const Canvas = styled.div`
  flex: 1 1 0;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  /* Hide the scrollbar visually while keeping scroll behavior */
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
    width: 0;
  }
`;

/** Inner content wrapper — provides gap between sections and bottom breathing room. */
export const ContentScroll = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${S.sectionGap};
  box-sizing: border-box;
  /* F05: 780 wide column, centred, padding 24 0 (the footer is its own strip, so no 120 bottom pad). */
  width: 100%;
  max-width: 48.75rem;
  margin: 0 auto;
  padding: ${spacing[6]} ${spacing[4]};
`;

/** F05 footer: an 84px strip on a grey-50 gradient, hint on the left, the Generate pill on the right. */
export const Footer = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing[4]};
  box-sizing: border-box;
  height: ${spacing[20]};
  min-height: 5.25rem;
  padding: 0 ${spacing[6]};
  background: linear-gradient(180deg, transparent 0%, ${rawColors.grey[50]} 100%);
  border-radius: 0 0 ${borderRadius.xl} ${borderRadius.xl};
`;

export const FooterHint = styled.p`
  margin: 0;
  ${typographyMixin("caption", 4)}
  color: ${rawColors.text.mediumEmphasis};
`;

/** F05 "Generate story" pill: 44px tall, padding 0 24, 14/600, lavender-tinted drop shadow. */
export const GenerateButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  height: ${spacing[11]};
  padding: 0 ${spacing[6]};
  border: none;
  border-radius: ${borderRadius.full};
  background: ${rawColors.brand.primary};
  color: ${rawColors.text.white};
  ${typographyMixin("body", 2)}
  box-shadow: 0 6px 16px -4px rgba(100, 66, 179, 0.4);
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${rawColors.primary[600]};
  }
  &:disabled {
    background: ${rawColors.grey[400]};
    box-shadow: none;
    cursor: not-allowed;
  }
`;

/** F05 readiness pill: 26px tall, padding 0 10, primary-100 fill, 12/600 primary-700. */
export const ReadinessPill = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  height: ${spacing[6]};
  min-height: 1.625rem;
  padding: 0 ${spacing[2.5]};
  border-radius: ${borderRadius.full};
  background: ${rawColors.primary[100]};
  color: ${rawColors.primary[700]};
  ${typographyMixin("caption", 2)}
  white-space: nowrap;
`;

export const PageTitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${spacing[4]};
`;

/* ─────────────────────────────────────────────
   Page header
───────────────────────────────────────────── */

export const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[1]};
  padding-bottom: ${S.headerBottomPad};
`;

/** F05: 22/30, weight 600. */
export const PageTitle = styled.p`
  margin: 0;
  ${typographyMixin("heading", "h4")}
  font-size: ${fontSizes[22]};
  font-weight: ${fontWeights.semibold};
  line-height: 1.875rem;
  color: ${rawColors.text.highEmphasis};
`;

/** F05: 13px, medium-emphasis. */
export const PageSubtitle = styled.p`
  margin: 0;
  ${typographyMixin("body", 4)}
  font-size: ${fontSizes[13]};
  color: ${rawColors.text.mediumEmphasis};
`;

/* ─────────────────────────────────────────────
   Shared section card
───────────────────────────────────────────── */

/** Light-purple section card wrapping each configuration row. */
export const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${S.sectionInnerGap};
  width: 100%;
  box-sizing: border-box;
  background: ${rawColors.structural.white};
  border: 1px solid ${rawColors.stroke[200]};
  border-radius: ${borderRadius["2xl"]};
  padding: ${S.sectionPadTop} 0 ${S.sectionPadBottom};
`;

export const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 ${S.sectionPadX};
  flex-shrink: 0;
  box-sizing: border-box;
  width: 100%;
`;

export const SectionTitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  /* Icons inside section headers always use interactive purple.
     SectionTitle has an explicit color so it overrides this inheritance. */
  color: ${rawColors.text.interactive};
`;

/** F05: section heading 13/700. */
export const SectionTitle = styled.p`
  margin: 0;
  ${typographyMixin("body", 1)}
  font-size: ${fontSizes[13]};
  color: ${rawColors.text.highEmphasis};
`;

/** F05 header action link ("Regenerate", "+ Add"): 12/600 brand primary, no chrome. */
export const SectionAction = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  ${typographyMixin("caption", 2)}
  color: ${rawColors.brand.primary};

  &:hover:not(:disabled) {
    color: ${rawColors.brand.primaryDark};
  }
  &:disabled {
    color: ${rawColors.text.lowEmphasis};
    cursor: not-allowed;
  }
`;

/** Goal and purpose stacked inside the one merged card. */
export const MergedNarratives = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
`;

export const SectionBody = styled.div`
  padding: 0 ${S.sectionPadX};
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
`;

/* ─────────────────────────────────────────────
   AI Edit button (Goal / Purpose rows)
───────────────────────────────────────────── */

/* ─────────────────────────────────────────────
   Banner illustration (shown during initial load)
───────────────────────────────────────────── */

export const BannerWrap = styled.div`
  display: flex;
  justify-content: center;
  padding-bottom: ${spacing[3]};
`;

export const BannerImg = styled.img`
  width: 347px;
  height: 220px;
  object-fit: contain;
`;

export const AiEditButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  padding: ${spacing[1]} ${spacing[2]};
  background: ${rawColors.structural.white};
  border: 1px solid ${rawColors.stroke[200]};
  border-radius: ${borderRadius.md};
  cursor: pointer;
  color: ${rawColors.text.mediumEmphasis};
  flex-shrink: 0;

  &:hover {
    border-color: ${rawColors.stroke[300]};
    color: ${rawColors.text.highEmphasis};
  }
`;

/**
 * Pen + sparkle badge overlay — matches Figma CSS grid pattern:
 * pen and sparkle share the same grid cell; sparkle offset ml-10/mt-10
 * puts it at the bottom-right corner of the pen.
 */
export const AiPenIconWrap = styled.span`
  position: relative;
  display: inline-flex;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
`;

export const AiSparkleBadge = styled.span`
  position: absolute;
  bottom: -3px;
  right: -3px;
  display: inline-flex;
  line-height: 0;
`;

/** Icon slot inside each AI dropdown item (Regenerate / narrative actions). */
export const AiDropdownItemIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  margin-right: ${spacing[2]};
  color: ${rawColors.text.interactive};
`;

export const BodyText = styled.p`
  margin: 0;
  ${typographyMixin("body", 4)}
  color: ${rawColors.text.highEmphasis};
  word-break: break-word;
`;

export const NarrativeTextarea = styled(StyledTextarea)`
  ${typographyMixin("body", 4)}
  color: ${rawColors.text.highEmphasis};
`;

/* ─────────────────────────────────────────────
   Audience chips
───────────────────────────────────────────── */

export const AudienceChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing[2]};
`;

/** F05 chip: 28px tall, primary-100 fill, 12/600 primary-700. */
export const AudienceChip = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[1.5]};
  height: ${spacing[7]};
  box-sizing: border-box;
  padding: 0 ${spacing[2.5]};
  background: ${rawColors.primary[100]};
  border-radius: ${borderRadius.full};
  flex-shrink: 0;
`;

export const ChipLabel = styled.p`
  margin: 0;
  ${typographyMixin("caption", 2)}
  color: ${rawColors.primary[700]};
  white-space: nowrap;
`;

export const ChipRemoveBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${rawColors.primary[700]};
  flex-shrink: 0;
  &:hover { color: ${rawColors.text.highEmphasis}; }
`;

/** F05 "+ Add" chip: dashed primary-200 border, brand-primary text. */
export const AddAudienceChip = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing[1]};
  height: ${spacing[7]};
  box-sizing: border-box;
  padding: 0 ${spacing[2.5]};
  background: none;
  border: 1px dashed ${rawColors.primary[200]};
  border-radius: ${borderRadius.full};
  color: ${rawColors.brand.primary};
  cursor: pointer;
  flex-shrink: 0;
  &:hover { border-color: ${rawColors.brand.primary}; }
`;

export const AddAudienceLabel = styled.p`
  margin: 0;
  ${typographyMixin("caption", 2)}
  color: ${rawColors.brand.primary};
  white-space: nowrap;
`;

/* ─────────────────────────────────────────────
   Format / Layout cards
───────────────────────────────────────────── */

export const CardGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[3]};
  width: 100%;
`;

/** F05 option button: 40px tall, radius 10; selected = 2px primary border on a theme-light fill. */
export const FormatCard = styled.button<{ $selected?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing[2]};
  flex: 0 1 auto;
  height: ${spacing[10]};
  min-width: 0;
  box-sizing: border-box;
  padding: 0 ${spacing[4]};
  background: ${({ $selected }) =>
    $selected ? rawColors.structural.themeLight : rawColors.structural.white};
  border: ${({ $selected }) =>
    $selected
      ? `2px solid ${rawColors.brand.primary}`
      : `1px solid ${rawColors.stroke[200]}`};
  border-radius: ${borderRadius[10]};
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: ${rawColors.primary[300]};
  }
  &:disabled {
    cursor: not-allowed;
    background: ${rawColors.grey[50]};
  }
`;

export const FormatIconWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing[1]};
  background: ${rawColors.primary[100]};
  border-radius: ${borderRadius.md};
  flex-shrink: 0;
  color: ${rawColors.primary[500]};
`;

export const FormatLabel = styled.p<{ $selected?: boolean }>`
  margin: 0;
  ${typographyMixin("caption", 2)}
  color: ${({ $selected }) => ($selected ? rawColors.primary[700] : rawColors.text.highEmphasis)};
  white-space: nowrap;
  text-align: center;
`;

/** F05: "Suggested: Presentation" beside the options, 11/600 low emphasis. */
export const SuggestedNote = styled.span`
  ${typographyMixin("caption", 2)}
  font-size: ${fontSizes[11]};
  color: ${rawColors.text.lowEmphasis};
  white-space: nowrap;
`;

/* ─────────────────────────────────────────────
   Layout cards
───────────────────────────────────────────── */

export const LayoutCard = styled.button<{ $selected?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${spacing[1]};
  flex: 1 1 130px;
  min-width: 120px;
  height: 94px;
  align-items: flex-start;
  justify-content: center;
  padding: ${spacing[1]} ${spacing[2]} ${spacing[1]};
  background: ${rawColors.structural.white};
  border: ${({ $selected }) =>
    $selected
      ? `2px solid ${rawColors.brand.primary}`
      : `1px solid ${rawColors.stroke[200]}`};
  border-radius: ${borderRadius[10]};
  background-color: ${({ $selected }) =>
    $selected ? rawColors.structural.themeLight : rawColors.structural.white};
  box-shadow: none;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: ${rawColors.primary[300]};
  }
`;

export const LayoutCardLabel = styled.p`
  margin: 0;
  ${typographyMixin("caption", 3)}
  color: ${rawColors.text.highEmphasis};
  white-space: nowrap;
`;

/* Thumbnail primitives shared across all layout previews */
export const ThumbWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  width: 100%;
  flex: 1 1 0;
  min-height: 0;
  overflow: hidden;
`;

export const ThumbRow = styled.div`
  display: flex;
  gap: 3px;
  align-items: stretch;
  width: 100%;
  flex-shrink: 0;
`;

export const ThumbBlock = styled.div<{
  $dark?: boolean;
  $h: number;
  $flex?: boolean;
  $widthPct?: string;
}>`
  height: ${({ $h }) => $h}px;
  ${({ $flex, $widthPct }) =>
    $flex
      ? "flex: 1 1 0; min-width: 0;"
      : $widthPct
        ? `width: ${$widthPct}; flex-shrink: 0;`
        : "flex: 1 1 0; min-width: 0;"}
  border-radius: ${borderRadius.sm};
  background-color: ${({ $dark }) =>
    $dark ? rawColors.primary[200] : rawColors.primary[100]};
  opacity: ${({ $dark }) => ($dark ? 0.7 : 0.8)};
`;

/* ─────────────────────────────────────────────
   Sources section
───────────────────────────────────────────── */

export const SegmentedControl = styled.div`
  display: flex;
  align-items: center;
  height: 40px;
  width: 100%;
  box-sizing: border-box;
  padding: ${spacing[1]};
  background: ${rawColors.structural.white};
  border: 1px solid ${rawColors.stroke[200]};
  border-radius: ${borderRadius[10]};
  overflow: hidden;
`;

export const SegmentTab = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1 1 0;
  min-width: 0;
  height: 32px;
  padding: ${spacing[1]} ${spacing[2]};
  background: ${({ $active }) =>
    $active ? rawColors.primary[100] : "transparent"};
  border: none;
  border-radius: ${borderRadius.sm};
  cursor: pointer;
  box-shadow: ${({ $active }) =>
    $active ? "1px 1px 0.5px rgba(89,98,112,0.07)" : "none"};
`;

export const SegmentLabel = styled.p<{ $active?: boolean }>`
  margin: 0;
  ${typographyMixin("caption", 3)}
  color: ${({ $active }) =>
    $active ? rawColors.primary[700] : rawColors.text.lowEmphasis};
  white-space: nowrap;
`;

export const SourceDropdown = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  box-sizing: border-box;
  height: 40px;
  padding: ${spacing[2]} ${spacing[3]};
  background: ${rawColors.structural.white};
  border: 1px solid ${rawColors.stroke[200]};
  border-radius: ${borderRadius[10]};
  cursor: pointer;

  &:hover {
    border-color: ${rawColors.stroke[300]};
  }
`;

export const DropdownLabel = styled.p`
  margin: 0;
  ${typographyMixin("body", 3)}
  color: ${rawColors.text.highEmphasis};
`;

/* ─────────────────────────────────────────────
   Data source multi-select chips + dropdown
   Reuses Item + Checkbox from MultiSelectSearch.styles
───────────────────────────────────────────── */

export const DataSourceLogoImg = styled.img`
  width: 16px;
  height: 16px;
  border-radius: ${borderRadius.sm};
  object-fit: contain;
  flex-shrink: 0;
`;

export const DataSourceLogoPlaceholder = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: ${borderRadius.sm};
  background: ${rawColors.primary[100]};
  ${typographyMixin("caption", 3)}
  color: ${rawColors.text.interactive};
  flex-shrink: 0;
`;

/* ─────────────────────────────────────────────
   Key areas of analysis
───────────────────────────────────────────── */

export const KeyAreaRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  width: 100%;
  box-sizing: border-box;
  padding: ${spacing[1]} ${spacing[2]};
  background: ${rawColors.structural.white};
  border: 1px solid ${rawColors.stroke[200]};
  border-radius: ${borderRadius[10]};
  flex-shrink: 0;
  min-width: 0;
`;

export const KeyAreaText = styled.p`
  margin: 0;
  /* Sits in KeyAreaBody's column: a zero flex-basis there collapses its height
     and the overflow clip hides the text, so it sizes to its content. */
  flex: 0 0 auto;
  min-width: 0;
  word-break: break-word;
  ${typographyMixin("body", 4)}
  color: ${rawColors.text.highEmphasis};
`;

export const KeyAreaRows = styled.div<{ $scrollable: boolean }>`
  display: ${({ $scrollable }) => ($scrollable ? "flex" : "contents")};
  min-height: 0;
  flex-direction: column;
  gap: ${spacing[2]};
  max-height: ${({ $scrollable }) =>
    $scrollable ? spacing[32] : "none"};
  overflow-y: ${({ $scrollable }) =>
    $scrollable ? "auto" : "visible"};
  scrollbar-color: ${rawColors.grey[500]} transparent;
  scrollbar-gutter: ${({ $scrollable }) =>
    $scrollable ? "stable" : "auto"};
  scrollbar-width: thin;
`;

export const KeyAreaRemoveBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${rawColors.text.lowEmphasis};
  flex-shrink: 0;
  &:hover { color: ${rawColors.text.mediumEmphasis}; }
`;

export const KeyAreaInput = styled.input`
  width: 100%;
  height: 40px;
  padding: 0 ${spacing[3]};
  background: ${rawColors.structural.white};
  border: 1px solid ${rawColors.stroke[200]};
  border-radius: ${borderRadius[10]};
  ${typographyMixin("caption", 4)}
  color: ${rawColors.text.highEmphasis};
  outline: none;
  box-sizing: border-box;

  &::placeholder {
    color: ${rawColors.text.lowEmphasis};
  }

  &:focus {
    border-color: ${rawColors.primary[500]};
  }
`;

/* ─────────────────────────────────────────────
   Clarifications
───────────────────────────────────────────── */

/* ─────────────────────────────────────────────
   Model dropdown
───────────────────────────────────────────── */

export const ModelDropdownWrapper = styled.div`
  position: relative;
  width: 100%;
`;

export const ModelDropdownList = styled.div<{ $fixed?: boolean }>`
  position: ${({ $fixed }) => ($fixed ? "fixed" : "absolute")};
  top: ${({ $fixed }) => ($fixed ? "unset" : `calc(100% + ${spacing[1]})`)};
  left: ${({ $fixed }) => ($fixed ? "unset" : "0")};
  right: ${({ $fixed }) => ($fixed ? "unset" : "0")};
  z-index: ${({ $fixed }) => ($fixed ? 1000 : 10)};
  background: ${rawColors.structural.white};
  border: 1px solid ${rawColors.stroke[200]};
  border-radius: ${borderRadius.lg};
  box-shadow: 0 4px 12px rgba(89, 98, 112, 0.12);
  overflow: hidden;
`;

export const ModelDropdownItem = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  padding: ${spacing[2]} ${spacing[3]};
  background: ${({ $active }) => ($active ? rawColors.primary[100] : "transparent")};
  border: none;
  cursor: pointer;
  text-align: left;
  ${typographyMixin("body", 4)}
  color: ${({ $active }) => ($active ? rawColors.text.interactive : rawColors.text.highEmphasis)};

  &:hover {
    background: ${rawColors.primary[50]};
  }

  /* A not-yet-wired action stays listed with a Coming soon tag, inert. */
  &[aria-disabled="true"] {
    gap: ${spacing[2]};
    cursor: default;
    color: ${rawColors.text.lowEmphasis};
    background: transparent;
  }
`;

export const ClarificationHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  padding: 0 ${S.sectionPadX};
`;

/** "1 of 1" beside the clarifying-question heading. */
export const ClarificationBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing[1]};
  flex-shrink: 0;
`;

/** F05: the open question sits on a 1.5px brand-primary card. */
export const ClarificationSection = styled(Section)`
  /* GAP: 1.5px has no border-width token (2px is the nearest) */
  border: 1.5px solid ${rawColors.brand.primary};
`;

/** Beside a section title: the plan picked this and the person hasn't changed it. */
export const SuggestedTag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0 ${spacing[2]};
  height: 20px;
  border-radius: ${borderRadius.md};
  background: ${rawColors.primary[100]};
  ${typographyMixin("caption", 3)}
  color: ${rawColors.text.interactive};
`;

export const BadgeText = styled.span`
  ${typographyMixin("caption", 2)}
  color: ${rawColors.text.mediumEmphasis};
`;

export const BadgeTextSub = styled.span`
  ${typographyMixin("caption", 2)}
  color: ${rawColors.text.mediumEmphasis};
`;

export const ClarificationQuestion = styled.p`
  margin: 0;
  ${typographyMixin("body", 3)}
  color: ${rawColors.text.highEmphasis};
`;

export const RadioRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${spacing[3]};
  padding: 0;
  width: 100%;
`;

/** F05 answer option: a 40px bordered button-like row; selected = 2px brand border. */
export const RadioLabel = styled.label<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  box-sizing: border-box;
  min-height: ${spacing[10]};
  padding: 0 ${spacing[3]};
  background: ${({ $selected }) =>
    $selected ? rawColors.structural.themeLight : rawColors.structural.white};
  border: ${({ $selected }) =>
    $selected
      ? `2px solid ${rawColors.brand.primary}`
      : `1px solid ${rawColors.stroke[200]}`};
  border-radius: ${borderRadius[10]};
  cursor: pointer;
  width: 100%;
`;

export const RadioInput = styled.input`
  margin: 0;
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  accent-color: ${rawColors.primary[500]};
  cursor: pointer;
`;

export const RadioLabelText = styled.p`
  margin: 0;
  ${typographyMixin("body", 4)}
  color: ${rawColors.text.highEmphasis};
  flex: 1;
`;

export const OtherInputWrap = styled.div`
  padding: 0;
  width: 100%;
  box-sizing: border-box;
  min-width: 0;
`;

export const OtherTextInput = styled.input`
  width: 100%;
  height: 40px;
  padding: 0 ${spacing[3]};
  background: ${rawColors.structural.white};
  border: 1px solid ${rawColors.stroke[200]};
  border-radius: ${borderRadius[10]};
  ${typographyMixin("caption", 4)}
  color: ${rawColors.text.highEmphasis};
  outline: none;
  box-sizing: border-box;

  &::placeholder {
    color: ${rawColors.text.lowEmphasis};
  }

  &:focus {
    border-color: ${rawColors.primary[500]};
  }
`;

export const ClarificationNav = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 ${S.sectionPadX};
`;

export const NavBackBtn = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  ${typographyMixin("body", 3)}
  color: ${rawColors.text.highEmphasis};
  &:disabled {
    color: ${rawColors.text.lowEmphasis};
    cursor: not-allowed;
  }
`;

export const NavActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
`;

export const SkipBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  padding: ${spacing[2]} ${spacing[4]};
  background: ${rawColors.structural.white};
  border: 1px solid ${rawColors.stroke[200]};
  border-radius: ${borderRadius.md};
  cursor: pointer;
  ${typographyMixin("caption", 3)}
  color: ${rawColors.text.mediumEmphasis};
  &:hover { border-color: ${rawColors.stroke[300]}; }
  &:disabled {
    border-color: ${rawColors.stroke[200]};
    color: ${rawColors.text.lowEmphasis};
    cursor: not-allowed;
  }
`;

export const NextBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  padding: ${spacing[2]} ${spacing[4]};
  background: ${rawColors.structural.white};
  border: 1px solid ${rawColors.text.interactive};
  border-radius: ${borderRadius.md};
  cursor: pointer;
  ${typographyMixin("caption", 3)}
  color: ${rawColors.text.interactive};
  &:hover { background: ${rawColors.primary[100]}; }
  &:disabled {
    border-color: ${rawColors.stroke[200]};
    color: ${rawColors.text.lowEmphasis};
    background: ${rawColors.structural.white};
    cursor: not-allowed;
  }
`;

/**
 * Manual agent editor — Figma 5397:216989.
 *
 * This extends the existing StoryBuilder instead of duplicating its fields or
 * interactions. Keeping the variant here makes the visual contract explicit
 * and prevents the automatic story-planner surface from changing.
 */
export const AgentCard = styled(Card)`
  padding: ${spacing[3]};
  border: 1px solid ${rawColors.stroke[200]};
  border-radius: ${borderRadius.xl};

  ${ProgressBar} {
    display: none;
  }

  ${ContentScroll} {
    gap: ${spacing[5]};
    max-width: none;
    margin: 0;
    padding: 0 0 ${spacing[5]};
  }

  ${PageHeader} {
    gap: 0;
    padding-bottom: 0;
  }

  ${PageTitle} {
    ${typographyMixin("subtitle", 2)}
  }

  ${Section} {
    gap: ${spacing[2]};
    padding: 0;
    background: transparent;
    border: none;
    border-radius: 0;
  }

  ${SectionHeader},
  ${SectionBody} {
    padding: 0;
  }

  ${SectionTitleGroup} {
    gap: 0;

    > svg {
      display: none;
    }
  }

  ${SectionTitle} {
    ${typographyMixin("caption", 3)}
    color: ${rawColors.text.mediumEmphasis};
  }

  ${NarrativeTextarea},
  ${KeyAreaInput} {
    box-shadow: ${shadows.elevation[1]};
  }

  [data-testid="section-goal"] ${NarrativeTextarea} {
    height: 2.75rem;
    padding: 0.625rem ${spacing[3]};
    overflow: hidden;
    resize: none;
  }

  [data-testid="section-purpose"] ${NarrativeTextarea} {
    height: 6.25rem;
    padding: 0.625rem ${spacing[3]};
    resize: none;
  }

  [data-testid="section-audience"] ${SectionBody} {
    padding: ${spacing[3]};
    border: 1px solid ${rawColors.structural[200]};
    border-radius: ${borderRadius.lg};
  }

  [data-testid="section-key-areas"] > div:last-child {
    padding-left: 0;
    padding-right: 0;
  }

  ${AddAudienceChip} {
    min-height: 2rem;
  }

  [data-testid="section-format"] ${CardGrid},
  [data-testid="section-layout"] ${CardGrid} {
    flex-wrap: nowrap;
  }

  ${FormatCard},
  ${LayoutCard} {
    flex-basis: 0;
    min-width: 0;
  }

  ${KeyAreaInput} {
    height: 2.75rem;
  }

  ${FooterHint} {
    display: none;
  }

  ${Footer} {
    display: flex;
    justify-content: flex-end;
    margin: 0 -${spacing[3]} -${spacing[3]};
    height: auto;
    min-height: 0;
    background: none;
    border-radius: 0;
    padding: ${spacing[3]};
    border-top: 1px solid ${rawColors.stroke[200]};

    > button {
      width: auto;
      min-width: 6.5rem;
    }
  }

  ${mediaQueries.down.md} {
    [data-testid="section-format"] ${CardGrid},
    [data-testid="section-layout"] ${CardGrid} {
      flex-wrap: wrap;
    }

    ${FormatCard},
    ${LayoutCard} {
      flex-basis: 8rem;
      min-width: 7.5rem;
    }
  }
`;
